<script setup lang="ts">
import type { ExcludeEmptyString } from "~/types/Utils";

import type { ContentSectionStoryblok } from "~/storyblok/.generated/component-types";

const {
  subtitle,
  desktopContentDirection = "column",
  textAlign = "center",
  backgroundColor = "platform-bg-1",
} = defineProps<{
  title: string;
  subtitle?: string;
  content: ContentSectionStoryblok["content"];
  desktopContentDirection?: ExcludeEmptyString<
    ContentSectionStoryblok["desktopContentDirection"]
  >;
  textAlign?: ExcludeEmptyString<ContentSectionStoryblok["textAlign"]>;
  backgroundColor?: ExcludeEmptyString<
    ContentSectionStoryblok["backgroundColor"]
  >;
}>();

const textAlignClass = computed(
  () =>
    ({
      left: "text-left",
      center: "text-center",
      right: "text-right",
    })[textAlign],
);

const contentDirectionClass = computed(
  () =>
    ({
      row: "row",
      column: "column",
    })[desktopContentDirection],
);
</script>

<template>
  <section
    :class="`bg-${backgroundColor}`"
    class="content-section-block flex justify-center"
  >
    <div class="base-block-spacing">
      <div
        v-if="title.length || subtitle?.length"
        class="w-viewport content-section-block__header mb-6 sm:mb-8 lg:mb-16 px-4 md:px-0"
      >
        <BaseTitle
          as="h2"
          :class="textAlignClass"
        >
          {{ title }}
        </BaseTitle>
        <p
          v-if="subtitle"
          :class="textAlignClass"
        >
          {{ subtitle }}
        </p>
      </div>

      <div
        v-if="content?.length"
        :class="{
          'nested-block-item-row': contentDirectionClass === 'row',
          [`content-section-block__container-${contentDirectionClass}`]: true,
          'nested-block-item-column': contentDirectionClass === 'column',
        }"
        class="content-section-block__container"
      >
        <Blok
          v-for="blok in content"
          :key="blok._uid"
          :blok="blok"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="postcss">
.content-section-block {
  &__header {
    @apply flex flex-col gap-4;
    @apply text-center;
    p {
      @apply text-base;
    }
  }
  &__container > * {
    @apply md:px-0;
  }
  &__container-row {
    @apply w-viewport;
    @apply m-auto grid gap-x-4;
    @apply grid-cols-1 md:grid-cols-2 lg:auto-cols-fr lg:grid-flow-col;
  }
  &__container-column {
    @apply flex flex-col;
  }
}
</style>
